// SupportThreadComposer (issue #423). The in-thread composer for SUPPORT
// threads, referenced by `SupportInbox.tsx`'s header comment but previously
// missing. Admins toggle between two modes:
//
//   - "Reply"  — a normal contractor-visible message. This delegates to the
//     existing `BottomBarContent` path untouched, so attachment upload, the
//     legacy socket emit, and the message-type handling all keep working.
//   - "Internal note" — an admins-only note. Calls `useAddSupportNote`
//     (`POST /chats/support/:chatId/internal-note`), which the backend
//     persists as a SYSTEM-kind message with `metadata.internal = true` and
//     strips from the contractor-facing reader. On success the hook
//     invalidates the chat's messages query so the note appears inline.
//
// Internal-note mode is visually distinguished (amber/warning surface, a
// visibility-off icon, "Visible to admins only" helper text) so an admin can
// never confuse it with a contractor-visible reply.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputBase,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material';
import { ReplyTwoTone, VisibilityOffTwoTone } from '@mui/icons-material';
import { toast } from 'react-toastify';

import BottomBarContent from './BottomBarContent';
import { useAddSupportNote } from 'src/queries/chat';

import type { Chat, ChatMessage } from 'src/models/chat';

type ComposerMode = 'reply' | 'note';

const NoteWrapper = styled(Box)(
  ({ theme }) => `
    background: ${theme.palette.warning.light}1f;
    border-top: 1px solid ${theme.palette.warning.main};
    padding: ${theme.spacing(2)};
  `
);

const NoteInput = styled(InputBase)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(16)};
    padding: ${theme.spacing(1, 1.5)};
    width: 100%;
    border-radius: ${theme.general.borderRadius};
    border: 1px solid ${theme.palette.warning.main};
    background: ${theme.colors.alpha.white[100]};
  `
);

interface SupportThreadComposerProps {
  supportScope?: 'legacy' | 'platform';
  canonicalTransport?: boolean;
  chat: Chat;
  messages: ChatMessage[];
  setMessages: (
    next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])
  ) => void;
  scrollToBottom: () => void;
  socket: unknown;
}

export default function SupportThreadComposer({
  chat,
  messages,
  setMessages,
  scrollToBottom,
  socket,
  supportScope = 'legacy',
  canonicalTransport = false,
}: SupportThreadComposerProps) {
  const [mode, setMode] = useState<ComposerMode>('reply');
  const [noteText, setNoteText] = useState('');
  const addNote = useAddSupportNote(supportScope);

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: ComposerMode | null
  ) => {
    // ToggleButtonGroup yields `null` when the active button is re-clicked;
    // ignore that so a mode is always selected.
    if (next) setMode(next);
  };

  const handleSendNote = () => {
    const content = noteText.trim();
    if (!content) return;
    addNote.mutate(
      { chatId: chat.id, content },
      {
        onSuccess: () => {
          setNoteText('');
          // The hook already invalidates the messages query; nudge the
          // scroll so the new note is visible once the refetch lands.
          scrollToBottom();
        },
        onError: (err) => {
          const message =
            err instanceof Error ? err.message : 'Failed to add internal note';
          toast.error(message);
        },
      }
    );
  };

  return (
    <Box data-testid="support-thread-composer">
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={handleModeChange}
          data-testid="support-composer-mode"
        >
          <ToggleButton value="reply" data-testid="support-composer-mode-reply">
            <ReplyTwoTone fontSize="small" sx={{ mr: 0.5 }} />
            Reply
          </ToggleButton>
          <ToggleButton
            value="note"
            data-testid="support-composer-mode-note"
            sx={{
              '&.Mui-selected': {
                color: 'warning.dark',
                bgcolor: 'warning.light',
              },
            }}
          >
            <VisibilityOffTwoTone fontSize="small" sx={{ mr: 0.5 }} />
            Internal note
          </ToggleButton>
        </ToggleButtonGroup>
        {mode === 'note' && (
          <Typography
            variant="caption"
            color="warning.dark"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <VisibilityOffTwoTone fontSize="inherit" sx={{ mr: 0.5 }} />
            Visible to admins only
          </Typography>
        )}
      </Box>

      {mode === 'reply' ? (
        // The normal composer path stays entirely intact for replies.
        <BottomBarContent
          chat={chat}
          messages={messages}
          setMessages={setMessages}
          scrollToBottom={scrollToBottom}
          socket={socket}
          supportScope={supportScope}
          canonicalTransport={canonicalTransport}
        />
      ) : (
        <NoteWrapper>
          <Alert
            severity="warning"
            icon={<VisibilityOffTwoTone fontSize="inherit" />}
            sx={{ mb: 1.5 }}
          >
            Internal notes are visible to admins only — the contractor never
            sees them.
          </Alert>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <NoteInput
              multiline
              maxRows={4}
              placeholder="Write an internal note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              inputProps={{ 'data-testid': 'support-note-input' }}
            />
            <Button
              variant="contained"
              color="warning"
              onClick={handleSendNote}
              disabled={!noteText.trim() || addNote.isLoading}
              startIcon={
                addNote.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <VisibilityOffTwoTone />
                )
              }
              data-testid="support-note-submit"
            >
              Add note
            </Button>
          </Stack>
        </NoteWrapper>
      )}
    </Box>
  );
}
